import { createRoot, Root } from 'react-dom/client';
import { App } from './App.js';
import { PromoApp } from './PromoApp.js';
import type { CartSlideInConfig, PromoSectionConfig } from './types.js';
import { buildConfig } from './config/buildConfig.js';
import { initCart, store, loadCartInfo } from '@sfly/cart-shared-library';
import type {
  UserInfo,
  RootState,
  TogglzFeature,
} from '@sfly/cart-shared-library';
import { getAuthInfo } from './services/authService.js';
import { getEnvConfig, goToCart } from './utils/commonUtil.js';
import { logInfo, logError } from './utils/logger.js';
import {
  CartVariant,
  readActivityNameFromTogglz,
  readResolvedVariant,
  resolveVariant,
} from './utils/variantResolver.js';

declare global {
  interface Window {
    cartSlideInMFE: {
      init: (config: CartSlideInConfig) => Promise<void>;
      open: () => void;
      destroy: () => void;
      getBuildConfig: () => typeof buildConfig;
      readResolvedVariant: () => CartVariant;
    };
    promoMFE: {
      init: (config: PromoSectionConfig) => Promise<void>;
      update: (
        data: Partial<
          Pick<
            PromoSectionConfig,
            'cartInfo' | 'promotionsDesc' | 'couponErrorMessage' | 'accountInfo'
          >
        >,
      ) => void;
      destroy: () => void;
      _debug?: {
        readonly config: PromoSectionConfig | null;
      };
    };
  }
}

let root: Root | null = null;
let mountedContainerId: string | null = null;
let cachedUserInfo: UserInfo | null = null;

function dispatchSilentRefresh() {
  if (!cachedUserInfo) return;
  // Mirror useCartData's hasData guard: when the store has no cart yet,
  // useCartData's first-mount effect will fire the initial load (with the
  // loader). Skipping here avoids a duplicate /cartinfo call on first init.
  const { cartInfo } = store.getState() as RootState;
  if (!cartInfo || !('_id' in cartInfo)) return;
  store.dispatch(loadCartInfo(cachedUserInfo, { silent: true }));
}

window.cartSlideInMFE = {
  // Mount and open immediately
  async init(config: CartSlideInConfig) {
    logInfo('Initializing cart slide-in MFE', 'InitCartMFE', { config });
    await mount(config);
    dispatchSilentRefresh();
  },

  // Reopen the panel after it was dismissed via the close button
  open() {
    dispatchSilentRefresh();
    if (mountedContainerId) {
      const container = document.getElementById(mountedContainerId);
      if (
        container &&
        (container as HTMLElement & { openCart: () => void }).openCart
      ) {
        (container as HTMLElement & { openCart: () => void }).openCart();
      }
    }
  },

  readResolvedVariant() {
    return readResolvedVariant();
  },

  destroy() {
    if (root) {
      root.unmount();
      root = null;
      mountedContainerId = null;
    }
  },

  getBuildConfig() {
    return buildConfig;
  },
};

let promoRoot: Root | null = null;
let currentPromoConfig: PromoSectionConfig | null = null;

window.promoMFE = {
  async init(config: PromoSectionConfig) {
    logInfo('Initializing promo MFE', 'InitPromoMFE', { config });
    await initializeLib();
    const container = document.getElementById(config.containerId);
    if (!container) {
      logError(
        `Container #${config.containerId} not found`,
        'InitPromoMFEError',
        {
          containerId: config.containerId,
        },
      );
      return;
    }
    if (promoRoot) {
      promoRoot.unmount();
    }
    currentPromoConfig = config;
    promoRoot = createRoot(container);
    promoRoot.render(<PromoApp config={config} />);
  },

  update(data) {
    if (!promoRoot || !currentPromoConfig) return;
    currentPromoConfig = { ...currentPromoConfig, ...data };
    promoRoot.render(<PromoApp config={currentPromoConfig} />);
  },

  destroy() {
    if (promoRoot) {
      promoRoot.unmount();
      promoRoot = null;
      currentPromoConfig = null;
    }
  },
};

//Added a debug getter for non-prod envs to know the current promo config
if (process.env.NODE_ENV !== 'prod') {
  Object.defineProperty(window.promoMFE, '_debug', {
    get: () => ({
      get config() {
        return currentPromoConfig;
      },
    }),
    enumerable: false,
  });
}

async function mount(config: CartSlideInConfig) {
  await initializeLib(config);

  if (config.layoutMode === 'default') {
    const container = document.getElementById(
      config.containerId,
    ) as HTMLDivElement | null;
    if (!container) {
      logError(
        `Container #${config.containerId} not found`,
        'MountCartMFEError',
        {
          containerId: config.containerId,
        },
      );
      return;
    }
    if (root) {
      root.unmount();
    }
    mountedContainerId = config.containerId;
    root = createRoot(container);
    root.render(<App config={config} />);
    return;
  }

  const envConfig = getEnvConfig();
  const discoveryConfig = {
    ...envConfig,
    ...(config.discoveryEndpoints ?? {}),
  };
  // popup mode: togglz remains the kill switch. If off, fall back to the
  // existing cart page without ever calling Adobe Target.
  if (
    config.layoutMode === 'popup' &&
    !isSlideInEnabled(
      config.togglzFlags ?? [],
      cachedUserInfo?.account_id as string,
    )
  ) {
    goToCart(discoveryConfig, config);
    return;
  }
  const activityName = readActivityNameFromTogglz(config.togglzFlags ?? []);
  const variant = await resolveVariant(activityName);

  if (variant !== 'C') {
    logInfo(
      'Popup mode resolved to non-slidein variant — redirecting to cart page',
      'CartVariantRedirect',
      { variant },
    );
    goToCart(discoveryConfig, config);
    return;
  }

  const container = document.createElement('div');
  container.id = `${config.containerId}-root`;
  document.body.appendChild(container);

  if (root) {
    root.unmount();
  }
  mountedContainerId = config.containerId;
  root = createRoot(container);
  root.render(<App config={config} />);
}

async function initializeLib(config: any = {}) {
  const userInfo: Record<string, unknown> = await getAuthInfo();
  const envConfig = getEnvConfig();
  const discoveryConfig = {
    ...envConfig,
    ...(config.discoveryEndpoints ?? {}),
  };
  cachedUserInfo = { ...userInfo, xApiKey: envConfig.cartApiKey } as UserInfo;

  initCart({
    loggerConfig: {
      appEnv: envConfig.appEnv,
      xApiKey: envConfig.cartApiKey,
      userId: userInfo.account_id,
    },
    cartConfig: {
      discoveryConfig: {
        cartURL: discoveryConfig.cartapi,
        accounts: discoveryConfig.accounts,
        retailUrl: discoveryConfig.retail,
        create: discoveryConfig.create,
        commerce: discoveryConfig.commerce,
        catalogproduct: discoveryConfig.catalogproduct,
        shopping: discoveryConfig.shopping,
        photosUrl: discoveryConfig.photosbaseurl,
        accountsFEUrl: discoveryConfig.accountsfe,
        catalogdesign: discoveryConfig.catalogdesign,
        projectsUrl: discoveryConfig.projects,
      },
      togglzFeatures: config.togglzFlags,
      layoutMode: config.layoutMode,
    },
  });
}

function isSlideInEnabled(togglzFlags: TogglzFeature[], accountId?: string) {
  const flagInfo = togglzFlags.find(
    (flag) => flag.uid === 'cart.cartapp.shutterfly-us.enable_slidein_cart',
  );
  const enabled = flagInfo?.enable ?? false;
  if (!enabled && accountId) {
    const userIds = flagInfo?.customProperties?.userId?.value ?? '';
    return userIds.includes(accountId);
  }
  return enabled;
}
